import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['track', 'progress', 'current', 'total']

  static values = {
    autoplayDelay: { type: Number, default: 5000 },
    slidesToShow: { type: Number, default: 3 }
  }

  connect() {
    this.currentIndex = 0
    this.totalSlides = this.trackTarget.children.length
    this.autoplayInterval = null

    this.updateUI()
    this.startAutoplay()

    // Pause autoplay on hover
    this.element.addEventListener('mouseenter', () => this.stopAutoplay())
    this.element.addEventListener('mouseleave', () => this.startAutoplay())

    // Handle resize for responsive slides
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    this.stopAutoplay()
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize() {
    // Clamp currentIndex if it's now out of bounds after resize
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex
    }
    this.updateUI()
  }

  get maxIndex() {
    // Maximum index where all visible slides are valid
    const visibleSlides = this.getVisibleSlides()
    return Math.max(0, this.totalSlides - visibleSlides)
  }

  get totalPages() {
    // Number of pages (groups of slides)
    const visibleSlides = this.getVisibleSlides()
    return Math.ceil(this.totalSlides / visibleSlides)
  }

  getVisibleSlides() {
    // Responsive: 1 on mobile, 2 on tablet, slidesToShowValue on desktop
    const width = window.innerWidth
    if (width < 768) return 1
    if (width < 1024) return 2
    return this.slidesToShowValue
  }

  next() {
    const visibleSlides = this.getVisibleSlides()
    const nextIndex = this.currentIndex + visibleSlides

    if (nextIndex <= this.maxIndex) {
      this.currentIndex = nextIndex
    } else if (this.currentIndex < this.maxIndex) {
      // Jump to last page if we're not already there
      this.currentIndex = this.maxIndex
    } else {
      // Loop back to start
      this.currentIndex = 0
    }
    this.updateUI()
  }

  prev() {
    const visibleSlides = this.getVisibleSlides()
    const prevIndex = this.currentIndex - visibleSlides

    if (prevIndex >= 0) {
      this.currentIndex = prevIndex
    } else if (this.currentIndex > 0) {
      // Jump to first page if we're not already there
      this.currentIndex = 0
    } else {
      // Loop to last page
      this.currentIndex = this.maxIndex
    }
    this.updateUI()
  }

  goTo(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (index >= 0 && index <= this.maxIndex) {
      this.currentIndex = index
      this.updateUI()
    } else if (index > this.maxIndex) {
      // If clicking beyond maxIndex, go to last valid position
      this.currentIndex = this.maxIndex
      this.updateUI()
    }
  }

  updateUI() {
    const visibleSlides = this.getVisibleSlides()
    const slideWidth = 100 / visibleSlides

    // Update track position
    if (this.hasTrackTarget) {
      this.trackTarget.style.transform = `translateX(-${this.currentIndex * slideWidth}%)`
    }

    // Update progress indicators - highlight currently visible slides
    if (this.hasProgressTargets) {
      this.progressTargets.forEach((segment, index) => {
        if (index >= this.currentIndex && index < this.currentIndex + visibleSlides) {
          segment.classList.add('active')
        } else {
          segment.classList.remove('active')
        }
      })
    }

    // Update current display (show current page number)
    if (this.hasCurrentTarget) {
      const currentPage = Math.floor(this.currentIndex / this.getVisibleSlides()) + 1
      this.currentTarget.textContent = String(currentPage).padStart(2, '0')
    }

    // Update total display (show total pages)
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = String(this.totalPages).padStart(2, '0')
    }
  }

  startAutoplay() {
    this.stopAutoplay()
    this.autoplayInterval = setInterval(() => {
      this.next()
    }, this.autoplayDelayValue)
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval)
      this.autoplayInterval = null
    }
  }
}
